import { useSyncExternalStore } from 'react';

export type ImpersonatedIdentity = Readonly<{
  name: string;
  roleLabel: string | null;
}>;

type BlockedMutation = Readonly<{ operationName: string; count: number }>;

type ImpersonationSnapshot = Readonly<{
  customerUUID: string | null;
  identity: ImpersonatedIdentity | null;
  writeModeEnabled: boolean;
  lastBlocked: BlockedMutation | null;
  revision: number;
}>;

export const READ_ONLY_BLOCK_PREFIX = 'Blocked while impersonating';

export const createImpersonationSession = () => {
  let snapshot: ImpersonationSnapshot = {
    customerUUID: null,
    identity: null,
    writeModeEnabled: false,
    lastBlocked: null,
    revision: 0,
  };
  let blockedCount = 0;
  const listeners = new Set<() => void>();
  const customerListeners = new Set<(customerUUID: string | null) => void>();
  const notify = () => listeners.forEach((listener) => listener());

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const getSnapshot = () => snapshot;
  const getImpersonatedCustomer = () => snapshot.customerUUID;
  const isImpersonatingSession = () => snapshot.customerUUID !== null;
  const isWriteModeEnabled = () => snapshot.writeModeEnabled;
  const areMutationsBlocked = () => isImpersonatingSession() && !snapshot.writeModeEnabled;

  const setImpersonatedCustomer = (customerUUID: string | null) => {
    if (snapshot.customerUUID === customerUUID) return;
    snapshot = {
      customerUUID,
      identity: null,
      writeModeEnabled: false,
      lastBlocked: null,
      revision: snapshot.revision + 1,
    };
    customerListeners.forEach((listener) => listener(customerUUID));
    notify();
  };

  const subscribeToImpersonatedCustomer = (listener: (customerUUID: string | null) => void) => {
    customerListeners.add(listener);
    return () => {
      customerListeners.delete(listener);
    };
  };

  const setWriteModeEnabled = (enabled: boolean) => {
    const writeModeEnabled = isImpersonatingSession() && enabled;
    if (snapshot.writeModeEnabled === writeModeEnabled) return;
    snapshot = {
      ...snapshot,
      writeModeEnabled,
      revision: snapshot.revision + (writeModeEnabled ? 0 : 1),
    };
    notify();
  };

  const confirmWriteMode = ({ revision }: { revision: number }) => {
    if (snapshot.revision === revision) setWriteModeEnabled(true);
  };

  const setImpersonatedCustomerIdentity = (identity: ImpersonatedIdentity | null) => {
    if (
      snapshot.identity?.name === identity?.name &&
      snapshot.identity?.roleLabel === identity?.roleLabel
    )
      return;
    snapshot = { ...snapshot, identity };
    notify();
  };

  const refuseWrite = (operationName: string) => {
    snapshot = { ...snapshot, lastBlocked: { operationName, count: ++blockedCount } };
    notify();
    return `${READ_ONLY_BLOCK_PREFIX}: ${operationName}`;
  };

  return {
    subscribe,
    getSnapshot,
    getImpersonatedCustomer,
    isImpersonatingSession,
    isWriteModeEnabled,
    areMutationsBlocked,
    setImpersonatedCustomer,
    subscribeToImpersonatedCustomer,
    setWriteModeEnabled,
    confirmWriteMode,
    setImpersonatedCustomerIdentity,
    refuseWrite,
  };
};

export type ImpersonationSession = ReturnType<typeof createImpersonationSession>;

export const useImpersonationSnapshot = (session: ImpersonationSession) =>
  useSyncExternalStore(session.subscribe, session.getSnapshot, session.getSnapshot);
